import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
    Clock,
    Search,
    Check,
    X,
    Trash2,
    AlertTriangle,
    CheckCircle2,
    XCircle,
    Info,
    ChevronLeft,
    ChevronRight,
    Users,
    Settings,
    Download,
    FileText
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const formatTeamName = (value) => {
    const team = String(value || "").trim();
    if (!team) return "No Team";
    return `${team.charAt(0).toUpperCase()}${team.slice(1)}`;
};

const buildBackendUrl = (path = "") => {
    if (!path || String(path).startsWith("data:") || String(path).startsWith("blob:")) return path;
    if (/^https?:\/\//i.test(path)) return path;

    const baseURL = String(api.defaults.baseURL || "").replace(/\/+$/, "");
    const normalizedPath = String(path).startsWith("/") ? path : `/${path}`;
    return `${baseURL}${normalizedPath}`;
};

const AdminLeave = () => {
    const navigate = useNavigate();
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [processing, setProcessing] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Custom Modal States
    const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
    const [leaveToReject, setLeaveToReject] = useState(null);
    const [rejectionReasonInput, setRejectionReasonInput] = useState("");
    const [reasonPreview, setReasonPreview] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [leaveToDelete, setLeaveToDelete] = useState(null);

    const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState(false);
    const [deleteAllStage, setDeleteAllStage] = useState(1);

    useEffect(() => {
        document.title = "Leave Management | Savrion Technologies";
        setCurrentPage(1);
    }, [filter, search, pageSize]);

    const fetchLeaves = useCallback(async () => {
        try {
            const res = await api.get("/api/admin/leave", { cache: false });
            setLeaves(res.data);
        } catch (err) {
            if ([401, 403].includes(err.response?.status)) navigate("/admin/login");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        document.title = "Leave Management | Savrion Technologies";
        fetchLeaves();
        
        // Poll for new leave applications every 10 seconds to provide instant reflection
        const intervalId = window.setInterval(fetchLeaves, 10000);
        return () => window.clearInterval(intervalId);
    }, [fetchLeaves]);

    const handleApprove = async (id, employeeId) => {
        setProcessing(id);
        try {
            await api.patch(`/api/admin/leave/${id}`, { status: "approved" });
            fetchLeaves();
        } catch (err) {
            alert("Failed to update status");
        } finally {
            setProcessing(null);
        }
    };

    const triggerRejectionFlow = (leave) => {
        setLeaveToReject(leave);
        setRejectionReasonInput("");
        setIsRejectionModalOpen(true);
    };

    const submitRejection = async () => {
        if (!rejectionReasonInput.trim() || !leaveToReject) return;
        setProcessing(leaveToReject._id);
        try {
            await api.patch(`/api/admin/leave/${leaveToReject._id}`, {
                status: "rejected",
                rejectionReason: rejectionReasonInput.trim()
            });
            setIsRejectionModalOpen(false);
            setLeaveToReject(null);
            fetchLeaves();
        } catch (err) {
            alert("Failed to reject application");
        } finally {
            setProcessing(null);
        }
    };

    const triggerDeleteFlow = (leave) => {
        setLeaveToDelete(leave);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteLeave = async () => {
        if (!leaveToDelete) return;
        setProcessing(leaveToDelete._id);
        try {
            await api.delete(`/api/admin/leave/${leaveToDelete._id}`);
            setIsDeleteModalOpen(false);
            setLeaveToDelete(null);
            fetchLeaves();
        } catch (err) {
            alert("Failed to delete record");
        } finally {
            setProcessing(null);
        }
    };

    const triggerDeleteAllFlow = () => {
        setDeleteAllStage(1);
        setIsDeleteAllModalOpen(true);
    };

    const confirmDeleteAll = async () => {
        setProcessing("all");
        try {
            await api.delete("/api/admin/leave");
            setIsDeleteAllModalOpen(false);
            fetchLeaves();
        } catch (err) {
            alert("Failed to purge leave database");
        } finally {
            setProcessing(null);
        }
    };

    const filteredLeaves = leaves.filter(l => {
        const matchesStatus = filter === "all" || l.status === filter;
        const matchesSearch = 
            l.employee?.name?.toLowerCase().includes(search.toLowerCase()) ||
            l.employee?.team?.toLowerCase().includes(search.toLowerCase());
        return matchesStatus && matchesSearch;
    });

    const effectivePageSize = pageSize === "all" ? 999999 : Number(pageSize);
    const totalPages = Math.max(1, Math.ceil(filteredLeaves.length / effectivePageSize));
    const indexOfLastItem = currentPage * effectivePageSize;
    const indexOfFirstItem = indexOfLastItem - effectivePageSize;
    const currentLeaves = filteredLeaves.slice(indexOfFirstItem, indexOfLastItem);

    const formatDate = (d) => new Date(d).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });

    const getAvatarGradient = (name) => {
        const charCode = (name || "E").charCodeAt(0);
        const gradients = [
            "from-indigo-500 to-violet-600 shadow-indigo-500/20",
            "from-purple-500 to-fuchsia-600 shadow-[var(--accent-primary)]/20",
            "from-blue-500 to-sky-600 shadow-[var(--accent-primary)]/20",
            "from-emerald-400 to-teal-600 shadow-emerald-500/20",
            "from-rose-400 to-pink-600 shadow-rose-500/20"
        ];
        return gradients[charCode % gradients.length];
    };

    // Calculate metrics
    const metrics = {
        total: leaves.length,
        pending: leaves.filter(l => l.status === "pending").length,
        approved: leaves.filter(l => l.status === "approved").length,
        rejected: leaves.filter(l => l.status === "rejected").length,
    };

    const handleExportExcel = () => {
        // Group leaves by employee name
        const grouped = {};
        filteredLeaves.forEach(l => {
            const empName = l.employee?.name || "Unknown Employee";
            if (!grouped[empName]) grouped[empName] = { team: formatTeamName(l.employee?.team), leaves: [], totalDays: 0 };
            const days = l.leaveDays || Math.max(1, Math.ceil((new Date(l.endDate) - new Date(l.startDate)) / 86400000) + 1);
            grouped[empName].leaves.push({ ...l, days });
            if (l.status === 'approved') grouped[empName].totalDays += days;
        });

        const data = [];
        Object.keys(grouped).sort().forEach(empName => {
            // Group Header Row
            data.push({
                "Employee Name": `▶ ${empName.toUpperCase()}`,
                "Team": grouped[empName].team,
                "Leave Type": `Approved Days: ${grouped[empName].totalDays}`,
                "Start Date": "",
                "End Date": "",
                "Requested Days": "",
                "Reason": "",
                "Status": "",
                "Applied On": ""
            });
            
            // Employee's leaves
            grouped[empName].leaves.forEach(l => {
                data.push({
                    "Employee Name": "", // Empty to un-congest the UI
                    "Team": "",
                    "Leave Type": String(l.leaveType).toUpperCase(),
                    "Start Date": new Date(l.startDate).toLocaleDateString(),
                    "End Date": new Date(l.endDate).toLocaleDateString(),
                    "Requested Days": l.days,
                    "Reason": l.reason || "",
                    "Status": String(l.status).toUpperCase(),
                    "Applied On": new Date(l.appliedOn).toLocaleDateString()
                });
            });
            
            // Blank row for separation
            data.push({});
        });

        const ws = XLSX.utils.json_to_sheet(data);
        
        // Auto-fit columns
        const colWidths = [
            { wch: 25 }, // Employee Name
            { wch: 15 }, // Team
            { wch: 18 }, // Leave Type
            { wch: 12 }, // Start Date
            { wch: 12 }, // End Date
            { wch: 15 }, // Requested Days
            { wch: 30 }, // Reason
            { wch: 12 }, // Status
            { wch: 12 }, // Applied On
        ];
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Grouped_Leaves");
        XLSX.writeFile(wb, `Leave_Report_Grouped_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const handleExportPDF = () => {
        const doc = new jsPDF();
        doc.text("Grouped Leave Report", 14, 15);
        
        const tableColumn = ["Leave Type", "Start", "End", "Days", "Status"];
        
        const grouped = {};
        filteredLeaves.forEach(l => {
            const empName = l.employee?.name || "Unknown Employee";
            if (!grouped[empName]) grouped[empName] = { team: formatTeamName(l.employee?.team), leaves: [], totalDays: 0 };
            const days = l.leaveDays || Math.max(1, Math.ceil((new Date(l.endDate) - new Date(l.startDate)) / 86400000) + 1);
            grouped[empName].leaves.push({ ...l, days });
            if (l.status === 'approved') grouped[empName].totalDays += days;
        });

        let currentY = 20;

        Object.keys(grouped).sort().forEach(empName => {
            const emp = grouped[empName];
            
            // Add Group Header Text
            doc.setFontSize(11);
            doc.setFont("helvetica", "bold");
            doc.text(`Employee: ${empName} (${emp.team}) - Total Approved Days: ${emp.totalDays}`, 14, currentY);
            currentY += 5;

            // Employee's leaves
            const tableRows = [];
            emp.leaves.forEach(l => {
                const rowData = [
                    String(l.leaveType).toUpperCase(),
                    new Date(l.startDate).toLocaleDateString(),
                    new Date(l.endDate).toLocaleDateString(),
                    l.days,
                    String(l.status).toUpperCase()
                ];
                tableRows.push(rowData);
            });

            autoTable(doc, {
                head: [tableColumn],
                body: tableRows,
                startY: currentY,
                theme: 'grid',
                headStyles: { fillColor: [16, 185, 129] }, // Emerald color to match theme
                margin: { bottom: 10 }
            });
            
            currentY = doc.lastAutoTable.finalY + 10;
            
            // If getting too close to bottom, add new page
            if (currentY > 270) {
                doc.addPage();
                currentY = 20;
            }
        });

        doc.save(`Leave_Report_Grouped_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-[var(--accent-primary)] border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm font-medium text-[var(--text-secondary)]">Loading leave requests...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="flex-1 min-w-0 overflow-auto">
                <div className="w-full space-y-5 p-3 sm:p-4 lg:p-6">
                
                {/* HERO */}
                <div className="bg-[var(--card-bg)] border border-[var(--border-color)] shadow-[var(--shadow-soft)] rounded-2xl p-5 sm:p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 h-40 w-40 bg-red-500/5 rounded-full blur-3xl -mr-12 -mt-12 group-hover:bg-red-500/10 transition-colors duration-500"></div>
                    <div className="z-10 min-w-0">
                        <span className="rounded-full bg-[var(--accent-subtle)] px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)]">
                            HR Portal
                        </span>
                        <h1 className="mt-1.5 text-2xl sm:text-3xl font-black tracking-tight text-[var(--text-primary)]">
                            Master Leave Management
                        </h1>
                        <p className="mt-1.5 text-xs sm:text-sm text-[var(--text-secondary)] font-semibold">
                            Oversee, approve, or reject employee leave applications across all departments.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 z-10 self-start md:self-center">
                        <button 
                            onClick={() => navigate('/admin/leave-settings')}
                            className="bg-[var(--accent-primary)] hover:bg-[var(--accent-primary)]/90 hover:shadow-[var(--accent-primary)]/25 hover:shadow-md text-white flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all border border-[var(--accent-primary)]/20 hover:-translate-y-0.5"
                        >
                            <Settings size={15} />
                            Manage Balances
                        </button>
                        {leaves.length > 0 && (
                            <>
                                <button 
                                    onClick={handleExportExcel}
                                    className="bg-emerald-500 hover:bg-emerald-600 hover:shadow-emerald-500/25 hover:shadow-md text-white flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all border border-emerald-500/20 hover:-translate-y-0.5"
                                >
                                    <Download size={15} />
                                    Export Excel
                                </button>
                                <button 
                                    onClick={handleExportPDF}
                                    className="bg-rose-500 hover:bg-rose-600 hover:shadow-rose-500/25 hover:shadow-md text-white flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all border border-rose-500/20 hover:-translate-y-0.5"
                                >
                                    <FileText size={15} />
                                    Export PDF
                                </button>
                            <button 
                                onClick={triggerDeleteAllFlow}
                                className="bg-red-500 hover:bg-red-600 hover:shadow-red-500/25 hover:shadow-md text-white flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-black text-xs uppercase tracking-wider transition-all border border-red-500/20 hover:-translate-y-0.5"
                            >
                                <Trash2 size={15} />
                                Delete All Leaves
                            </button>
                            </>
                        )}
                    </div>
                </div>

                {/* STATS METRICS GRID */}
                <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {[
                        { title: "Total Applications", value: metrics.total, icon: <Users size={20} />, color: "text-[var(--accent-primary)] dark:text-[var(--accent-primary)]", bg: "bg-[var(--accent-primary)]/10 border-[var(--accent-primary)]/20" },
                        { title: "Pending Review", value: metrics.pending, icon: <Clock size={20} />, color: "text-amber-500 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
                        { title: "Approved Leaves", value: metrics.approved, icon: <CheckCircle2 size={20} />, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
                        { title: "Rejected Leaves", value: metrics.rejected, icon: <XCircle size={20} />, color: "text-rose-500 dark:text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" },
                    ].map((item, i) => (
                        <div key={i} className="rounded-2xl bg-[var(--card-bg)] p-4 border border-[var(--border-color)] hover:border-[var(--accent-primary)]/30 hover:shadow-md transition-all duration-300 flex items-center justify-between group">
                            <div className="space-y-1 min-w-0">
                                <p className="text-[9px] font-black text-[var(--text-secondary)] uppercase tracking-widest truncate">{item.title}</p>
                                <h2 className="text-2xl font-black tracking-tight text-[var(--text-primary)] leading-none">{item.value}</h2>
                            </div>
                            <div className={`w-10 h-10 rounded-xl border ${item.bg} ${item.color} flex items-center justify-center shrink-0`}>
                                {item.icon}
                            </div>
                        </div>
                    ))}
                </section>

                {/* FILTERS & SEARCH ROW */}
                <section className="grid grid-cols-1 xl:grid-cols-[320px_1fr] gap-3">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] opacity-60" size={17} />
                        <input 
                            placeholder="Search employee or team..." 
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            className="w-full bg-[var(--card-bg)] border border-[var(--border-color)] pl-11 pr-4 py-2.5 rounded-xl outline-none text-xs sm:text-sm font-semibold transition-all focus:border-[var(--accent-primary)]/60 focus:ring-2 focus:ring-[var(--accent-primary)]/10"
                        />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 bg-[var(--card-bg)] p-1.5 border border-[var(--border-color)] rounded-xl">
                        {[
                            { id: "all", label: "All Logs", count: metrics.total },
                            { id: "pending", label: "Pending", count: metrics.pending },
                            { id: "approved", label: "Approved", count: metrics.approved },
                            { id: "rejected", label: "Rejected", count: metrics.rejected }
                        ].map(t => (
                            <button
                                key={t.id}
                                onClick={() => setFilter(t.id)}
                                className={`flex min-h-[38px] items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-black uppercase tracking-wide transition-all ${
                                    filter === t.id 
                                        ? "bg-[var(--accent-primary)] text-white shadow-sm" 
                                        : "bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] hover:text-[var(--text-primary)]"
                                }`}
                            >
                                <span className="truncate">{t.label}</span>
                                <span className={`inline-flex min-w-6 items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-black ${
                                    filter === t.id 
                                        ? "bg-[var(--card-bg)]/25 text-white" 
                                        : "bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-secondary)]"
                                }`}>
                                    {t.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </section>

                {/* REQUESTS TABLE */}
                <section className="overflow-hidden rounded-xl border border-[var(--border-color)] bg-[var(--card-bg)] shadow-[var(--shadow-soft)]">
                    {filteredLeaves.length === 0 ? (
                        <div className="p-10 text-center">
                            <Clock size={38} className="mx-auto mb-3 text-[var(--text-secondary)] opacity-20" />
                            <p className="text-sm font-black uppercase tracking-widest text-[var(--text-secondary)]">No leave requests found</p>
                            <p className="mt-1 text-xs font-semibold text-[var(--text-secondary)]">
                                {filter === "pending" && metrics.total > 0
                                    ? "There are no pending approvals right now. Check approved leaves or all logs."
                                    : "There are no records matching your current filter."}
                            </p>
                            {filter === "pending" && metrics.total > 0 && (
                                <div className="mt-5 flex flex-wrap justify-center gap-2">
                                    {metrics.approved > 0 && (
                                        <button
                                            type="button"
                                            onClick={() => setFilter("approved")}
                                            className="rounded-lg bg-emerald-500 px-4 py-2 text-xs font-black uppercase tracking-wider text-white"
                                        >
                                            View Approved ({metrics.approved})
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setFilter("all")}
                                        className="rounded-lg bg-[var(--accent-primary)] px-4 py-2 text-xs font-black uppercase tracking-wider text-white"
                                    >
                                        View All Logs ({metrics.total})
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1000px] border-collapse text-sm">
                                <colgroup>
                                    <col style={{ width: "22%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "20%" }} />
                                    <col style={{ width: "12%" }} />
                                    <col style={{ width: "6%" }} />
                                </colgroup>
                                <thead className="bg-[var(--bg-primary)]">
                                    <tr>
                                        {["Employee", "Type", "Days", "From", "To", "Reason", "Status", "Actions"].map((head) => (
                                            <th
                                                key={head}
                                                className="border border-[var(--border-color)] px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-[var(--text-secondary)]"
                                            >
                                                {head}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentLeaves.map((l) => (
                                        <tr key={l._id} className="transition-colors hover:bg-[var(--bg-primary)]">
                                            <td className="border border-[var(--border-color)] px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br ${getAvatarGradient(l.employee?.name)} text-sm font-black text-white shadow-sm`}>
                                                        {l.employee?.name?.charAt(0)}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="truncate font-extrabold text-[var(--text-primary)]">{l.employee?.name || "Unknown"}</div>
                                                        <div className="mt-0.5 flex items-center gap-1.5">
                                                            <span className="rounded-md border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[var(--text-secondary)]">
                                                                {formatTeamName(l.employee?.team)}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="border border-[var(--border-color)] px-4 py-3">
                                                <span className="rounded-md bg-[var(--accent-primary)]/10 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)]">
                                                    {l.leaveType}
                                                </span>
                                            </td>
                                            <td className="whitespace-nowrap border border-[var(--border-color)] px-4 py-3 font-bold text-[var(--text-primary)]">
                                                <div className="flex flex-col gap-0.5">
                                                    <span>{Math.max(1, Math.ceil((new Date(l.endDate) - new Date(l.startDate)) / (1000 * 60 * 60 * 24)) + 1)} Days</span>
                                                    {Number(l.requestedAllocation?.lossOfPay) > 0 && (
                                                        <span className="text-[9px] text-red-500 font-bold uppercase tracking-wider">({l.requestedAllocation.lossOfPay} LOP)</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="whitespace-nowrap border border-[var(--border-color)] px-4 py-3 font-bold text-[var(--text-primary)]">
                                                {formatDate(l.startDate)}
                                            </td>
                                            <td className="whitespace-nowrap border border-[var(--border-color)] px-4 py-3 font-bold text-[var(--text-primary)]">
                                                {formatDate(l.endDate)}
                                            </td>
                                            <td className="border border-[var(--border-color)] px-4 py-3">
                                                <div className="max-w-[280px]">
                                                    <button
                                                        type="button"
                                                        onClick={() => l.reason && setReasonPreview(l)}
                                                        className="group block w-full text-left"
                                                        aria-label={`Read full leave reason for ${l.employee?.name || "employee"}`}
                                                    >
                                                        <span className="block truncate text-xs font-semibold text-[var(--text-secondary)] group-hover:text-[var(--accent-primary)]">
                                                            {l.reason || "-"}
                                                        </span>
                                                        {l.reason && (
                                                            <span className="mt-1 inline-block text-[9px] font-black uppercase tracking-wider text-[var(--accent-primary)]">
                                                                Read more
                                                            </span>
                                                        )}
                                                    </button>
                                                    {l.rejectionReason && (
                                                        <p className="mt-1 truncate text-[10px] font-bold text-red-500" title={l.rejectionReason}>
                                                            Note: {l.rejectionReason}
                                                        </p>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="border border-[var(--border-color)] px-4 py-3">
                                                <div className="flex flex-col items-start gap-1">
                                                    <span className={`rounded-md border px-3 py-1 text-[10px] font-black uppercase tracking-wider ${
                                                        l.status === "approved"
                                                            ? "border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                                            : l.status === "rejected"
                                                                ? "border-red-500/25 bg-red-500/10 text-red-600 dark:text-red-400"
                                                                : "border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-400"
                                                    }`}>
                                                        {l.status}
                                                    </span>
                                                    {l.status !== "pending" && (
                                                        <div className="flex flex-col items-start mt-0.5">
                                                            <span className="text-[9px] font-semibold text-[var(--text-secondary)]">
                                                                {formatDate(l.processedAt || l.updatedAt)}
                                                            </span>
                                                            {l.processedBy && (
                                                                <span className="text-[9px] font-bold text-[var(--text-secondary)]">
                                                                    By: {l.processedBy.name} ({l.processedBy.role === 'admin' ? 'Admin' : 'Team Lead'})
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="border border-[var(--border-color)] px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {l.status === "pending" && (
                                                        <>
                                                            <button
                                                                disabled={processing === l._id}
                                                                onClick={() => {
                                                                    const normalDays = (l.requestedAllocation?.casualLeave || 0) + (l.requestedAllocation?.medicalLeave || 0) + (l.requestedAllocation?.compensatoryLeave || 0);
                                                                    const lopDays = l.requestedAllocation?.lossOfPay || 0;
                                                                    const calculatedDays = Math.max(1, Math.ceil((new Date(l.endDate) - new Date(l.startDate)) / (1000 * 60 * 60 * 24)) + 1);
                                                                    navigate(`/admin/leave-settings?employee=${l.employee?._id}&leaveId=${l._id}&leaveType=${l.leaveType}&leaveDays=${calculatedDays}&normalDays=${normalDays}&lopDays=${lopDays}`);
                                                                }}
                                                                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[var(--accent-primary)]/10 px-3 text-xs font-black uppercase tracking-wide text-[var(--accent-primary)] transition-colors hover:bg-[var(--accent-primary)] hover:text-white disabled:opacity-50"
                                                                title="Verify Leave Balances"
                                                            >
                                                                <Info size={13} />
                                                                Verify
                                                            </button>
                                                            <button
                                                                disabled={processing === l._id}
                                                                onClick={() => handleApprove(l._id, l.employee?._id)}
                                                                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-emerald-500 px-3 text-xs font-black uppercase tracking-wide text-white transition-colors hover:bg-emerald-600 disabled:opacity-50"
                                                            >
                                                                <Check size={13} />
                                                                Approve
                                                            </button>
                                                            <button
                                                                disabled={processing === l._id}
                                                                onClick={() => triggerRejectionFlow(l)}
                                                                className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-red-500 px-3 text-xs font-black uppercase tracking-wide text-white transition-colors hover:bg-red-600 disabled:opacity-50"
                                                            >
                                                                <X size={13} />
                                                                Reject
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        disabled={processing === l._id}
                                                        onClick={() => triggerDeleteFlow(l)}
                                                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-500/15 bg-red-500/5 text-red-500 transition-colors hover:bg-red-500 hover:text-white disabled:opacity-50"
                                                        title="Delete Leave Record"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>

                {/* PAGINATION */}
                {filteredLeaves.length > 0 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-[var(--card-bg)] border border-[var(--border-color)] px-4 py-3 rounded-2xl shadow-[var(--shadow-soft)]">
                        <div className="flex flex-wrap items-center gap-3">
                            <span className="text-xs font-semibold text-[var(--text-secondary)]">
                                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredLeaves.length)} of {filteredLeaves.length} applications
                            </span>
                            <div className="flex items-center gap-1.5 border-l border-[var(--border-color)] pl-3">
                                <span className="text-xs font-bold text-[var(--text-secondary)]">Show per page:</span>
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(e.target.value === "all" ? "all" : Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    className="h-8 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-2 text-xs font-bold outline-none focus:border-[var(--accent-primary)] cursor-pointer"
                                >
                                    <option value={10}>10</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value="all">All</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:bg-[var(--border-color)] rounded-xl text-xs font-bold disabled:opacity-40 transition-colors cursor-pointer"
                            >
                                <ChevronLeft size={15} /> Prev
                            </button>
                            <span className="text-xs font-bold px-2 text-[var(--text-primary)]">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                disabled={currentPage === totalPages}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-[var(--bg-secondary)] border border-[var(--border-color)] hover:bg-[var(--border-color)] rounded-xl text-xs font-bold disabled:opacity-40 transition-colors cursor-pointer"
                            >
                                Next <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                )}
                </div>
            </main>

            {/* ===== CUSTOM MODALS PORTALS (AnimatePresence) ===== */}
            <AnimatePresence>
                {reasonPreview && (
                    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="leave-reason-title">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setReasonPreview(null)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            className="relative z-10 w-full max-w-lg rounded-3xl border border-[var(--border-color)] bg-[var(--card-bg)] p-6 shadow-2xl"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-[var(--accent-primary)]">Leave reason</p>
                                    <h3 id="leave-reason-title" className="mt-1 text-lg font-extrabold text-[var(--text-primary)]">
                                        {reasonPreview.employee?.name || "Employee"}
                                    </h3>
                                    <p className="mt-1 text-xs font-semibold text-[var(--text-secondary)]">
                                        {formatDate(reasonPreview.startDate)} to {formatDate(reasonPreview.endDate)}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setReasonPreview(null)}
                                    className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                                    aria-label="Close reason popup"
                                >
                                    <X size={17} />
                                </button>
                            </div>
                            <div className="mt-5 max-h-[55vh] overflow-y-auto whitespace-pre-wrap break-words rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4 text-sm font-semibold leading-6 text-[var(--text-primary)]">
                                {reasonPreview.reason}
                            </div>
                            {reasonPreview.rejectionReason && (
                                <div className="mt-3 rounded-2xl border border-red-500/20 bg-red-500/10 p-4 text-sm font-semibold text-red-600 dark:text-red-300">
                                    <strong className="block text-[10px] uppercase tracking-wider">Admin note</strong>
                                    <span className="mt-1 block whitespace-pre-wrap">{reasonPreview.rejectionReason}</span>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}

                {/* 1. REJECTION REASON MODAL */}
                {isRejectionModalOpen && leaveToReject && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsRejectionModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        {/* Modal Box */}
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="bg-[var(--card-bg)] border border-[var(--border-color)] w-full max-w-md rounded-3xl p-6 shadow-2xl z-10 relative overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 w-full h-1 bg-red-500"></div>
                            
                            <div className="flex items-start gap-4 mb-4">
                                <div className="w-10 h-10 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
                                    <XCircle size={20} />
                                </div>
                                <div>
                                    <h3 className="font-extrabold text-lg text-[var(--text-primary)]">Reject Leave Application</h3>
                                    <p className="text-xs text-[var(--text-secondary)] mt-1 font-semibold">
                                        Please provide a rejection note for {leaveToReject.employee?.name}.
                                    </p>
                                </div>
                            </div>

                            <textarea
                                value={rejectionReasonInput}
                                onChange={e => setRejectionReasonInput(e.target.value)}
                                placeholder="Enter reason for rejection here..."
                                rows="3"
                                className="w-full bg-[var(--bg-primary)] border border-[var(--border-color)] p-3.5 rounded-2xl outline-none text-sm font-semibold focus:border-red-500/50 resize-none"
                            />

                            <div className="flex gap-3 mt-5 justify-end">
                                <button
                                    onClick={() => setIsRejectionModalOpen(false)}
                                    className="px-4 py-2.5 rounded-xl border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submitRejection}
                                    disabled={!rejectionReasonInput.trim()}
                                    className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 disabled:opacity-50 hover:shadow-red-500/20 hover:shadow-md text-white text-xs font-black uppercase tracking-wider transition-all"
                                >
                                    Confirm Rejection
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* 2. CONFIRM DELETE SINGLE REQUEST MODAL */}
                {isDeleteModalOpen && leaveToDelete && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsDeleteModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        {/* Modal Box */}
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="bg-[var(--card-bg)] border border-[var(--border-color)] w-full max-w-sm rounded-3xl p-6 shadow-2xl z-10 relative overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 w-full h-1 bg-red-500"></div>
                            
                            <div className="flex items-start gap-4 mb-4">
                                <div className="w-10 h-10 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
                                    <AlertTriangle size={20} />
                                </div>
                                <div>
                                    <h3 className="font-extrabold text-base text-[var(--text-primary)]">Delete Leave Record?</h3>
                                    <p className="text-xs text-[var(--text-secondary)] mt-1.5 font-semibold leading-relaxed">
                                        Are you sure you want to permanently delete this leave request for <span className="text-[var(--text-primary)] font-bold">{leaveToDelete.employee?.name}</span>?
                                    </p>
                                </div>
                            </div>

                            <p className="text-[10px] text-red-500 font-extrabold bg-red-500/5 p-3 rounded-xl border border-red-500/10 italic">
                                This action is permanent and cannot be undone.
                            </p>

                            <div className="flex gap-3 mt-5 justify-end">
                                <button
                                    onClick={() => setIsDeleteModalOpen(false)}
                                    className="px-4 py-2.5 rounded-xl border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDeleteLeave}
                                    className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 hover:shadow-red-500/20 hover:shadow-md text-white text-xs font-black uppercase tracking-wider transition-all"
                                >
                                    Delete Record
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}

                {/* 3. CONFIRM DELETE ALL LEAVES (STAGE-BASED WARNING MODAL) */}
                {isDeleteAllModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        {/* Backdrop */}
                        <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsDeleteAllModalOpen(false)}
                            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                        />
                        {/* Modal Box */}
                        <motion.div 
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            transition={{ type: "spring", duration: 0.4 }}
                            className="bg-[var(--card-bg)] border border-[var(--border-color)] w-full max-w-md rounded-3xl p-6 shadow-2xl z-10 relative overflow-hidden"
                        >
                            <div className="absolute top-0 left-0 w-full h-1 bg-red-500 animate-pulse"></div>

                            {deleteAllStage === 1 ? (
                                <div>
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-10 h-10 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
                                            <AlertTriangle size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-extrabold text-lg text-[var(--text-primary)]">Delete All Leave Logs?</h3>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 font-semibold leading-relaxed">
                                                WARNING: Are you absolutely sure you want to permanently delete ALL leave requests from the system?
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-red-500/5 p-4 rounded-2xl border border-red-500/10 flex gap-2.5 items-start">
                                        <Info size={15} className="text-red-500 shrink-0 mt-0.5" />
                                        <p className="text-[10px] text-red-500 font-bold leading-relaxed">
                                            This action will clear all historical applications, processed logs, and pending requests. This cannot be reverted.
                                        </p>
                                    </div>

                                    <div className="flex gap-3 mt-6 justify-end">
                                        <button
                                            onClick={() => setIsDeleteAllModalOpen(false)}
                                            className="px-4 py-2.5 rounded-xl border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] transition-all"
                                        >
                                            Abort Action
                                        </button>
                                        <button
                                            onClick={() => setDeleteAllStage(2)}
                                            className="px-4 py-2.5 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-black uppercase tracking-wider transition-all"
                                        >
                                            Continue anyway
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex items-start gap-4 mb-4">
                                        <div className="w-10 h-10 rounded-2xl bg-red-500/10 text-red-500 flex items-center justify-center shrink-0">
                                            <Trash2 size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-extrabold text-lg text-[var(--text-primary)]">Critical Safety Check</h3>
                                            <p className="text-xs text-[var(--text-secondary)] mt-1 font-semibold leading-relaxed">
                                                CRITICAL WARNING: Please re-confirm that you want to delete ALL leave requests.
                                            </p>
                                        </div>
                                    </div>
                                    
                                    <div className="bg-red-500/10 p-4 rounded-2xl border border-red-500/20 flex gap-2.5 items-start">
                                        <AlertTriangle size={15} className="text-red-500 shrink-0 mt-0.5" />
                                        <p className="text-[10px] text-red-600 dark:text-red-400 font-extrabold leading-relaxed uppercase tracking-wider">
                                            Final Confirmation Required. Click "Wipe Logs" to execute the delete commands.
                                        </p>
                                    </div>

                                    <div className="flex gap-3 mt-6 justify-end">
                                        <button
                                            onClick={() => setIsDeleteAllModalOpen(false)}
                                            className="px-4 py-2.5 rounded-xl border border-[var(--border-color)] hover:bg-[var(--bg-secondary)] text-xs font-black uppercase tracking-wider text-[var(--text-secondary)] transition-all"
                                        >
                                            Abort
                                        </button>
                                        <button
                                            onClick={confirmDeleteAll}
                                            className="px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 hover:shadow-red-600/25 hover:shadow-lg text-white text-xs font-black uppercase tracking-wider transition-all"
                                        >
                                            Wipe Logs
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default AdminLeave;


